import { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { trackPixelCustom } from "../lib/metaPixel";
import SEO from '@/components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Support = () => {
  const [stats, setStats] = useState({
    total_funds: 0,
    contribution_count: 0,
    volunteer_count: 0,
    elders_trained: 0,
    home_visits: 0
  });
  const [supporters, setSupporters] = useState([]);
  const [topDonors, setTopDonors] = useState({ month: '', is_current_month: false, donors: [] });
  const [showContributeModal, setShowContributeModal] = useState(false);
  const [showVolunteerModal, setShowVolunteerModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // Form states
  const [contributeForm, setContributeForm] = useState({
    name: "",
    email: "",
    phone: "",
    amount: "",
    pan: "",
    address: "",
    city: "",
    message: ""
  });

  const [volunteerForm, setVolunteerForm] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    experience: "",
    availability: ""
  });

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (e) {
      console.error("Error fetching stats:", e);
    }
  };

  const fetchSupporters = async () => {
    try {
      const response = await axios.get(`${API}/contributions`);
      setSupporters(response.data);
    } catch (e) {
      console.error("Error fetching supporters:", e);
    }
  };

  const fetchTopDonors = async () => {
    try {
      const response = await axios.get(`${API}/contributions/top-monthly`);
      setTopDonors(response.data);
    } catch (e) {
      console.error("Error fetching top donors:", e);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchSupporters();
    fetchTopDonors();
    
    // Check for payment status in URL params
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const orderId = urlParams.get('order_id');
    
    if (paymentStatus === 'success') {
      toast.success("Thank you for your contribution! 🙏");
      fetchStats();
      fetchSupporters();
      fetchTopDonors();
      // Meta Pixel: Donate is a custom event (not a Meta standard, but widely used for non-profits)
      const amount = parseInt(urlParams.get('amount'), 10);
      trackPixelCustom('Donate', {
        content_name: 'Giant-Tree Foundation Donation',
        content_category: 'Donation',
        content_ids: [orderId || 'unknown'],
        value: Number.isFinite(amount) ? amount : undefined,
        currency: 'INR',
      });
      setTimeout(() => setShowShareModal(true), 1000);
      // Clean URL
      window.history.replaceState({}, document.title, "/donate");
    } else if (paymentStatus === 'failed') {
      toast.error("Payment failed. Please try again.");
      // Clean URL
      window.history.replaceState({}, document.title, "/donate");
    }
    
    // Refresh stats and supporters every 30 seconds
    const interval = setInterval(() => {
      fetchStats();
      fetchSupporters();
      fetchTopDonors();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleContribute = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Create payment order with Cashfree
      const response = await axios.post(`${API}/contribute/payment`, {
        name: contributeForm.name,
        email: contributeForm.email,
        phone: contributeForm.phone || '0000000000',
        amount: parseInt(contributeForm.amount),
        pan: contributeForm.pan || '',
        address: contributeForm.address || '',
        city: contributeForm.city,
        message: contributeForm.message
      });
      
      const { order_id, payment_session_id } = response.data;
      
      // Initialize Cashfree Checkout
      const { load } = await import('@cashfreepayments/cashfree-js');
      const cashfree = await load({ mode: 'production' });
      
      // Open checkout
      const checkoutOptions = {
        paymentSessionId: payment_session_id,
        returnUrl: `${BACKEND_URL}/api/payment/return?order_id=${order_id}`,
        redirectTarget: '_self'
      };

      // Meta Pixel: track when the user is sent to the Cashfree donation page
      trackPixelCustom('InitiateDonation', {
        content_name: 'Giant-Tree Foundation Donation',
        content_category: 'Donation',
        value: parseInt(contributeForm.amount, 10),
        currency: 'INR',
      });

      cashfree.checkout(checkoutOptions).then(() => {
        console.log('Payment initiated');
      });
      
      setShowContributeModal(false);
      setContributeForm({ name: "", email: "", phone: "", amount: "", pan: "", address: "", city: "", message: "" });
      
    } catch (e) {
      const msg = e?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg, { duration: 6000 });
      console.error(e);
      setLoading(false);
    }
  };

  const handleVolunteer = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/volunteer`, volunteerForm);
      toast.success("Welcome to the team! We'll contact you soon.");
      setShowVolunteerModal(false);
      setVolunteerForm({ name: "", email: "", phone: "", city: "", experience: "", availability: "" });
      fetchStats();
      // Show share modal after successful volunteer signup
      setTimeout(() => setShowShareModal(true), 500);
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleShare = (platform) => {
    const shareText = "Help India's seniors navigate the digital world safely! Join Elderra's mission to train elders against online frauds and teach them to use modern apps. Every ₹1000 trains 1 senior. Support now!";
    const shareUrl = "https://elderra.io/donate";
    
    let url = "";
    switch(platform) {
      case "whatsapp":
        url = `https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`;
        break;
      case "twitter":
        url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
        break;
      case "linkedin":
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
        break;
      case "general":
        // Use Web Share API if available, otherwise copy to clipboard
        if (navigator.share) {
          navigator.share({
            title: 'Support Elderra - Help India\'s Seniors',
            text: shareText,
            url: shareUrl
          }).catch(console.error);
          closeShareAndScrollToSupporters();
          return;
        } else {
          // Fallback: copy link to clipboard
          navigator.clipboard.writeText(shareText + " " + shareUrl);
          toast.success("Link copied to clipboard!");
          closeShareAndScrollToSupporters();
          return;
        }
      default:
        return;
    }
    
    window.open(url, '_blank', 'noopener,noreferrer');
    closeShareAndScrollToSupporters();
  };

  const closeShareAndScrollToSupporters = () => {
    setShowShareModal(false);
    // Refresh data to show latest donation
    fetchStats();
    fetchSupporters();
    // Scroll to supporters section after a short delay
    setTimeout(() => {
      const supportersSection = document.getElementById('supporters-section');
      if (supportersSection) {
        supportersSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 300);
  };

  const handleShareSupporter = (supporter, platform) => {
    const shareText = `${supporter.name} just supported Elderra's mission to protect India's seniors! "${supporter.message || 'Helping elders stay safe online'}" - Join us in making a difference. Every ₹1000 helps 1 senior.`;
    const shareUrl = window.location.href;
    
    let url = "";
    switch(platform) {
      case "whatsapp":
        url = `https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`;
        break;
      case "twitter":
        url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
        break;
      case "linkedin":
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="support-page">
      <SEO 
        title="Support Elderra Foundation - Elderra | Empower & Protect Our Parents"
        description="Support Elderra Foundation to empower our parents and protect them from fraud. Support Elderra's mission to provide digital literacy training, fraud awareness, and everyday app guidance to India's seniors."
        keywords="support Elderra Foundation, protect parents from fraud, digital literacy donation, support elderly training, Elderra protection plan, fraud awareness seniors"
        ogImage="https://customer-assets.emergentagent.com/job_ab32166f-e481-4e95-8429-3d2fdf4d805e/artifacts/qjyx1n9t_Screen%20Shot%202025-11-21%20at%201.07.59%20PM.png"
        ogUrl="https://elderra.io/support"
      />
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="logo-container">
            <img 
              src="https://customer-assets.emergentagent.com/job_digital-seniors-3/artifacts/fqevhgzz_Elderra%20logo%20new%20original%20%281%29.png" 
              alt="Elderra.io Logo" 
              className="logo-image"
              data-testid="logo"
            />
          </div>
          <h2 className="hero-title" data-testid="hero-title">
            Support Elderra Foundation to empower our parents and protect them from fraud
          </h2>
          <p className="hero-subtitle" data-testid="hero-subtitle">
            Help us train senior citizens to navigate online safely, avoid frauds,
            and confidently use modern apps for everyday needs
          </p>
          
          {/* Live Counters */}
          <div className="stats-grid">
            <div className="stat-card" data-testid="stat-funds">
              <div className="stat-value">{formatCurrency(stats.total_funds)}</div>
              <div className="stat-label">Funds Raised</div>
            </div>
            <div className="stat-card" data-testid="stat-contributions">
              <div className="stat-value">{stats.contribution_count}</div>
              <div className="stat-label">Contributors</div>
            </div>
            <div className="stat-card" data-testid="stat-volunteers">
              <div className="stat-value">{stats.volunteer_count}</div>
              <div className="stat-label">Volunteers</div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="cta-container">
            <Button 
              className="cta-button cta-primary" 
              size="lg"
              onClick={() => setShowContributeModal(true)}
              data-testid="contribute-button"
            >
              Contribute Funds
            </Button>
            <Button 
              className="cta-button cta-secondary" 
              size="lg" 
              variant="outline"
              onClick={() => setShowVolunteerModal(true)}
              data-testid="volunteer-button"
            >
              Join as Volunteer
            </Button>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="impact-section">
        <div className="container">
          <h3 className="section-title" data-testid="impact-title">Your Impact</h3>
          <div className="impact-grid">
            <div className="impact-card" data-testid="impact-elders">
              <div className="impact-number">{stats.elders_trained}</div>
              <div className="impact-text">Elderly People to Receive Elderra's Protection Plan</div>
            </div>
          </div>
          <div className="impact-info" data-testid="impact-info">
            <p>Elderra's Protection plan includes training on fraud awareness, Introduction to everyday apps, and Elderra community groups.</p>
          </div>
        </div>
      </section>

      {/* Top Supporters Ribbon */}
      {topDonors.donors.length > 0 && (
        <section className="top-donors-ribbon" data-testid="top-donors-ribbon">
          <div className="container">
            <div className="top-donors-header">
              <span className="top-donors-badge" data-testid="top-donors-badge">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                {topDonors.is_current_month ? `Top Supporters · ${topDonors.month}` : 'Top Supporters'}
              </span>
            </div>
            <div className="top-donors-scroll">
              {topDonors.donors.map((donor, i) => (
                <div key={i} className="top-donor-chip" data-testid={`top-donor-chip-${i}`}>
                  <span className="top-donor-rank">#{i + 1}</span>
                  <span className="top-donor-avatar">{(donor.name || '?').charAt(0).toUpperCase()}</span>
                  <div className="top-donor-detail">
                    <span className="top-donor-name">{(donor.name || '').split(' ')[0]}</span>
                    <span className="top-donor-amount">{formatCurrency(donor.amount)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Supporters Section */}
      <section className="supporters-section" id="supporters-section">
        <div className="container">
          <h3 className="section-title" data-testid="supporters-title">Supporters So Far</h3>
          <p className="supporters-subtitle">
            Share these inputs online so that more people get to know about this initiative
          </p>
          
          {supporters.length === 0 ? (
            <div className="no-supporters">
              <p>Be the first to support this cause!</p>
            </div>
          ) : (
            <div className="supporters-grid">
              {supporters.map((supporter, index) => (
                <div key={supporter.id || index} className="supporter-card" data-testid={`supporter-card-${index}`}>
                  <div className="supporter-header">
                    <div className="supporter-avatar">
                      {supporter.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="supporter-info">
                      <h4 className="supporter-name">{supporter.name.split(' ')[0]}</h4>
                      <p className="supporter-amount">{formatCurrency(supporter.amount)}</p>
                    </div>
                  </div>
                  {supporter.message && (
                    <p className="supporter-message">"{supporter.message}"</p>
                  )}
                  <div className="supporter-footer">
                    <span className="supporter-time">
                      {new Date(supporter.timestamp).toLocaleDateString('en-IN', { 
                        day: 'numeric', 
                        month: 'short', 
                        year: 'numeric' 
                      })}
                    </span>
                    <div className="supporter-share-buttons">
                      <button 
                        className="share-icon-btn"
                        onClick={() => handleShareSupporter(supporter, 'whatsapp')}
                        title="Share on WhatsApp"
                        data-testid={`share-supporter-whatsapp-${index}`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                        </svg>
                      </button>
                      <button 
                        className="share-icon-btn"
                        onClick={() => handleShareSupporter(supporter, 'twitter')}
                        title="Share on Twitter"
                        data-testid={`share-supporter-twitter-${index}`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                        </svg>
                      </button>
                      <button 
                        className="share-icon-btn"
                        onClick={() => handleShareSupporter(supporter, 'linkedin')}
                        title="Share on LinkedIn"
                        data-testid={`share-supporter-linkedin-${index}`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Mission Section */}
      <section className="mission-section">
        <div className="container">
          <div className="mission-content-centered">
            <h3 className="section-title-center">Our Mission</h3>
            <p className="mission-description-center">
              India's senior population faces increasing challenges in the digital world. 
              From online frauds to struggling with basic apps like food delivery and cab booking,
              many elders feel left behind.
            </p>
            <p className="mission-description-center">
              Elderra's training program equips seniors with essential digital skills,
              helping them stay safe online and enjoy the convenience of modern technology.
            </p>
            <div className="mission-features-grid">
              <div className="feature-item">
                <span className="feature-icon">🛡️</span>
                <span>Online Fraud Prevention</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">📱</span>
                <span>App Navigation Training</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">🏠</span>
                <span>Personalized Home Visits</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">👥</span>
                <span>Community Support</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Community Photo Section */}
      <section className="photo-collage-section">
        <div className="container">
          <h2 className="section-title-center">Our Community in Action</h2>
          <p className="section-subtitle-center">
            See the impact we're making together - empowering India's senior citizens with digital literacy
          </p>
          
          <div style={{display: 'flex', justifyContent: 'center', marginTop: '2rem'}}>
            <div style={{maxWidth: '800px', width: '100%', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.1)'}}>
              <img 
                src="https://customer-assets.emergentagent.com/job_ab32166f-e481-4e95-8429-3d2fdf4d805e/artifacts/9wia3m0n_Screen%20Shot%202025-10-07%20at%207.34.07%20AM.png" 
                alt="Elderra community training session with senior citizens"
                style={{width: '100%', height: 'auto', display: 'block'}}
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Modals */}
      <Dialog open={showContributeModal} onOpenChange={setShowContributeModal}>
        <DialogContent className="contribute-modal compact-form" data-testid="contribute-modal">
          <DialogHeader>
            <DialogTitle>Donate to Giant-Tree Foundation</DialogTitle>
            <DialogDescription>
              Help train seniors in digital safety. 80G tax receipt included.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleContribute} className="modal-form donation-form">
            {/* Amount Selection - Most Important First */}
            <div className="form-group amount-highlight">
              <label htmlFor="contribute-amount">Donation Amount (₹) *</label>
              <div className="amount-presets">
                {[500, 1000, 2500, 5000].map(amt => (
                  <button
                    key={amt}
                    type="button"
                    className={`amount-preset-btn ${contributeForm.amount === String(amt) ? 'active' : ''}`}
                    onClick={() => setContributeForm({...contributeForm, amount: String(amt)})}
                  >
                    ₹{amt.toLocaleString()}
                  </button>
                ))}
              </div>
              <Input
                id="contribute-amount"
                type="number"
                min="100"
                data-testid="contribute-amount-input"
                value={contributeForm.amount}
                onChange={(e) => setContributeForm({...contributeForm, amount: e.target.value})}
                placeholder="Or enter custom amount"
                required
              />
              <small className="helper-text">₹1000 = 1 elder trained</small>
            </div>

            {/* Personal Details - Two Column Layout */}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="contribute-name">Full Name *</label>
                <Input
                  id="contribute-name"
                  data-testid="contribute-name-input"
                  value={contributeForm.name}
                  onChange={(e) => setContributeForm({...contributeForm, name: e.target.value})}
                  placeholder="As per PAN"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="contribute-phone">Phone *</label>
                <Input
                  id="contribute-phone"
                  type="tel"
                  data-testid="contribute-phone-input"
                  value={contributeForm.phone}
                  onChange={(e) => setContributeForm({...contributeForm, phone: e.target.value})}
                  placeholder="10-digit number"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="contribute-email">Email *</label>
                <Input
                  id="contribute-email"
                  type="email"
                  data-testid="contribute-email-input"
                  value={contributeForm.email}
                  onChange={(e) => setContributeForm({...contributeForm, email: e.target.value})}
                  placeholder="For receipt"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="contribute-pan">PAN (for 80G)</label>
                <Input
                  id="contribute-pan"
                  data-testid="contribute-pan-input"
                  value={contributeForm.pan}
                  onChange={(e) => setContributeForm({...contributeForm, pan: e.target.value.toUpperCase()})}
                  placeholder="ABCDE1234F"
                  maxLength={10}
                />
              </div>
            </div>

            {/* Comment Field - Visible */}
            <div className="form-group">
              <label htmlFor="contribute-message">Comment about the campaign</label>
              <Textarea
                id="contribute-message"
                data-testid="contribute-message-input"
                value={contributeForm.message}
                onChange={(e) => setContributeForm({...contributeForm, message: e.target.value})}
                placeholder="Share why you're supporting this cause..."
                rows={2}
              />
            </div>

            {/* Optional Fields - Collapsible */}
            <details className="optional-fields">
              <summary>Add address for receipt (optional)</summary>
              <div className="form-row">
                <div className="form-group">
                  <Input
                    id="contribute-address"
                    data-testid="contribute-address-input"
                    value={contributeForm.address}
                    onChange={(e) => setContributeForm({...contributeForm, address: e.target.value})}
                    placeholder="Address"
                  />
                </div>
                <div className="form-group">
                  <Input
                    id="contribute-city"
                    data-testid="contribute-city-input"
                    value={contributeForm.city}
                    onChange={(e) => setContributeForm({...contributeForm, city: e.target.value})}
                    placeholder="City"
                  />
                </div>
              </div>
            </details>

            <Button 
              type="submit" 
              className="submit-button donate-btn"
              disabled={loading}
              data-testid="contribute-submit-button"
            >
              {loading ? "Processing..." : `Donate ₹${contributeForm.amount ? parseInt(contributeForm.amount).toLocaleString() : '0'}`}
            </Button>
            <p className="secure-note">🔒 Secure payment via UPI, Cards, Net Banking</p>
          </form>
        </DialogContent>
      </Dialog>

      {/* Volunteer Modal */}
      <Dialog open={showVolunteerModal} onOpenChange={setShowVolunteerModal}>
        <DialogContent className="volunteer-modal" data-testid="volunteer-modal">
          <DialogHeader>
            <DialogTitle>Join as a Volunteer</DialogTitle>
            <DialogDescription>
              Help us conduct training sessions using the Elderra curriculum
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleVolunteer} className="modal-form">
            <div className="form-group">
              <label htmlFor="volunteer-name">Full Name *</label>
              <Input
                id="volunteer-name"
                data-testid="volunteer-name-input"
                value={volunteerForm.name}
                onChange={(e) => setVolunteerForm({...volunteerForm, name: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="volunteer-email">Email *</label>
              <Input
                id="volunteer-email"
                type="email"
                data-testid="volunteer-email-input"
                value={volunteerForm.email}
                onChange={(e) => setVolunteerForm({...volunteerForm, email: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="volunteer-phone">Phone Number *</label>
              <Input
                id="volunteer-phone"
                type="tel"
                data-testid="volunteer-phone-input"
                value={volunteerForm.phone}
                onChange={(e) => setVolunteerForm({...volunteerForm, phone: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="volunteer-city">City *</label>
              <Input
                id="volunteer-city"
                data-testid="volunteer-city-input"
                value={volunteerForm.city}
                onChange={(e) => setVolunteerForm({...volunteerForm, city: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="volunteer-experience">Teaching/Training Experience</label>
              <Textarea
                id="volunteer-experience"
                data-testid="volunteer-experience-input"
                value={volunteerForm.experience}
                onChange={(e) => setVolunteerForm({...volunteerForm, experience: e.target.value})}
                placeholder="Tell us about your experience (if any)..."
                rows={2}
              />
            </div>
            <div className="form-group">
              <label htmlFor="volunteer-availability">Availability</label>
              <Input
                id="volunteer-availability"
                data-testid="volunteer-availability-input"
                value={volunteerForm.availability}
                onChange={(e) => setVolunteerForm({...volunteerForm, availability: e.target.value})}
                placeholder="e.g., Weekends, Evenings"
              />
            </div>
            <Button 
              type="submit" 
              className="submit-button"
              disabled={loading}
              data-testid="volunteer-submit-button"
            >
              {loading ? "Submitting..." : "Join as Volunteer"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Share Modal - Thank You Popup */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="share-modal thank-you-modal" data-testid="share-modal">
          <DialogHeader>
            <DialogTitle className="thank-you-title">🎉 Big Thank You!</DialogTitle>
            <DialogDescription className="thank-you-message">
              Big thank you for your contribution! Can we please request you to help us find more supporters - on average, just one share on social media helps us find three more contributors.
            </DialogDescription>
          </DialogHeader>
          <div className="share-buttons">
            <Button 
              className="share-button whatsapp-share-btn"
              onClick={() => handleShare("whatsapp")}
              data-testid="share-whatsapp-button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              Share on WhatsApp
            </Button>
            <Button 
              className="share-button social-share-btn"
              onClick={() => handleShare("general")}
              data-testid="share-social-button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3"></circle>
                <circle cx="6" cy="12" r="3"></circle>
                <circle cx="18" cy="19" r="3"></circle>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
              </svg>
              Share on Social Media
            </Button>
          </div>
          <Button 
            variant="outline" 
            onClick={() => closeShareAndScrollToSupporters()}
            className="skip-button"
            data-testid="skip-share-button"
          >
            Maybe Later
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Support;
